import React, { useCallback, useMemo } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { useIntl } from "react-intl";

import { Group } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Popconfirm } from "@mapmycustomers/ui";

import styles from "./GroupMenu.module.scss";

interface Props {
  group?: Group;
  onDelete?: () => void;
  onDownload?: () => void;
  onRename?: () => void;
}

const GroupMenu: React.FC<Props> = ({ group, onDelete, onDownload, onRename }) => {
  const intl = useIntl();

  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean();

  const handleDeleteReasonClick = useCallback(
    (e) => {
      stopEvents(e);
      onDelete?.();
    },
    [onDelete]
  );

  const handleCancelClick = useCallback(
    (e) => {
      stopEvents(e);
      hideDeleteConfirmation();
    },
    [hideDeleteConfirmation]
  );

  const handleDeleteClick = useCallback(
    (e) => {
      stopEvents(e);
      showDeleteConfirmation();
    },
    [showDeleteConfirmation]
  );

  const handleMenuVisibilityChange = useCallback(
    (visibility: boolean) => {
      if (!visibility) {
        hideDeleteConfirmation();
      }
    },
    [hideDeleteConfirmation]
  );

  const menu: MenuProps = useMemo(
    () => ({
      items: [
        {
          disabled: !onDownload,
          key: "download",
          label: intl.formatMessage({
            id: "map.groups.sidebar.title.menu.download",
            defaultMessage: "Download",
            description: "Download records in Group View mode",
          }),
          onClick: onDownload,
        },
        {
          disabled: !group?.accessStatus.update,
          key: "rename",
          label: intl.formatMessage({
            id: "map.groups.sidebar.title.menu.editName",
            defaultMessage: "Edit Name",
            description: "Rename menu item in Group View mode",
          }),
          onClick: onRename,
        },
        {
          disabled: !group?.accessStatus.delete,
          key: "delete",
          label: (
            <Popconfirm
              cancelText={intl.formatMessage({
                id: "map.groups.sidebar.title.menu.delete.confirm.cancel",
                defaultMessage: "Cancel",
                description: "Delete confirm cancel text menu item in Group View mode",
              })}
              className={styles.deleteItem}
              description={intl.formatMessage({
                id: "map.groups.sidebar.title.menu.delete.confirm.title",
                defaultMessage: "Are you sure you want to permanently delete this group?",
                description: "Delete confirm title menu item in Group View mode",
              })}
              okText={intl.formatMessage({
                id: "map.groups.sidebar.title.menu.delete.confirm.ok",
                defaultMessage: "Delete Group",
                description: "Delete confirm ok text menu item in Group View mode",
              })}
              onCancel={handleCancelClick}
              onConfirm={handleDeleteReasonClick}
              open={deleteConfirmationVisible}
              placement="left"
            >
              <div onClick={handleDeleteClick}>
                {intl.formatMessage({
                  id: "map.groups.sidebar.title.menu.delete",
                  defaultMessage: "Delete Group",
                  description: "Delete menu item in Group View mode",
                })}
              </div>
            </Popconfirm>
          ),
        },
      ],
    }),
    [
      deleteConfirmationVisible,
      group?.accessStatus,
      handleCancelClick,
      handleDeleteClick,
      handleDeleteReasonClick,
      intl,
      onDownload,
      onRename,
    ]
  );

  if (!group?.accessStatus.update && !group?.accessStatus.delete) {
    return null;
  }

  return (
    <Dropdown menu={menu} onVisibleChange={handleMenuVisibilityChange} trigger={["click"]}>
      <FontAwesomeIcon className={styles.menuIcon} icon={faEllipsisH} />
    </Dropdown>
  );
};

export default GroupMenu;
